import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from 'reactstrap';
import { useCreateHelp } from 'api/helps/service';
import { getActiveColorStyle } from '../../../utils/colorUtils';
import Select from "components/Select";

/**
 * HelpMenu component for the help modal
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onToggle - Toggle handler for the modal
 * @param {function} onHoverChange - Hover state change handler
 * @param {object} style - Custom styles for the button
 * @param {string} iconColor - Color for the icon
 * @param {string} activeColor - The active theme color
 */
const HelpMenu = ({
  isOpen,
  onToggle,
  onHoverChange,
  style,
  iconColor,
  activeColor,
  isLightTheme
}) => {
  const [formData, setFormData] = useState({
    type: 'help',
    priority: 'medium',
    note: ''
  });

  const { mutate: createHelp, isLoading } = useCreateHelp();
  const activeStyle = getActiveColorStyle(activeColor);

  const typeOptions = [
    { value: 'bug', label: 'Bug' },
    { value: 'help', label: 'Help' },
    { value: 'info', label: 'Info' },
    { value: 'question', label: 'Question' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleMouseEnter = () => {
    onHoverChange?.(true);
  };

  const handleMouseLeave = () => {
    onHoverChange?.(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = () => {
    createHelp(formData, {
      onSuccess: () => {
        onToggle(false);
        setFormData({
          type: 'help',
          priority: 'medium',
          note: ''
        });
      }
    });
  };

  return (
    <>
      <Button
        color="secondary"
        size="sm"
        style={{
          width: 36,
          height: 36,
          padding: 0,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          ...style
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => onToggle(!isOpen)}
      >
        <i className="tim-icons icon-support-17" />
      </Button>

      <Modal isOpen={isOpen} toggle={() => onToggle(!isOpen)} modalClassName={!isLightTheme ? "modal-black" : ""}>
        <div className="modal-header justify-content-center" style={{
          backgroundImage: activeStyle.backgroundImage,
          color: 'white',
          borderTopLeftRadius: '0.2857rem',
          borderTopRightRadius: '0.2857rem',
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '15px'
        }}>
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => onToggle(!isOpen)}
            style={{ color: 'white', opacity: 1, textShadow: 'none', position: 'absolute', right: '15px', top: '15px' }}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <div style={{
            width: '50px',
            height: '50px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className="tim-icons icon-support-17" style={{ fontSize: '24px' }} />
          </div>
          <h4 className="title title-up" style={{ color: 'white', margin: 0, fontWeight: 600 }}>Help Request</h4>
        </div>
        <ModalBody>
          <Form>
            <div style={{ display: "flex", gap: "20px" }}>
              <FormGroup style={{ flex: 1 }}>
                <Label for="type">Type</Label>
                <Select
                  activeColor={activeColor}
                  className="react-select"
                  placeholder="Select Type"
                  name="type"
                  value={typeOptions.find(opt => opt.value === formData.type)}
                  onChange={(option) => {
                    setFormData(prev => ({ ...prev, type: option?.value || 'help' }));
                  }}
                  options={typeOptions}
                />
              </FormGroup>
              <FormGroup style={{ flex: 1 }}>
                <Label for="priority">Priority</Label>
                <Select
                  activeColor={activeColor}
                  className="react-select"
                  placeholder="Select Priority"
                  name="priority"
                  value={priorityOptions.find(opt => opt.value === formData.priority)}
                  onChange={(option) => {
                    setFormData(prev => ({ ...prev, priority: option?.value || 'medium' }));
                  }}
                  options={priorityOptions}
                />
              </FormGroup>
            </div>
            <FormGroup>
              <Label for="note">Note</Label>
              <Input type="textarea" name="note" id="note" value={formData.note} onChange={handleInputChange} rows="5" />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => onToggle(!isOpen)}>Cancel</Button>
          <Button
            style={{
              backgroundImage: activeStyle.backgroundImage,
              borderColor: activeStyle.borderColor,
              color: 'white'
            }}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
export default HelpMenu;